import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { FounderSidebar } from "@/components/FounderSidebar";
import { BranchSwitcher } from "@/components/BranchSwitcher";
import { BranchProvider } from "@/contexts/BranchContext";
import { useAuth } from "@/hooks/useAuth";
import { ReturningUserBanner } from "@/components/ReturningUserBanner";
import { useIsPro } from "@/hooks/useIsPro";
import { PaywallModalProvider } from "@/contexts/PaywallModalContext";
import { WelcomeToProScreen } from "@/components/WelcomeToProScreen";
import { GracePeriodBanner } from "@/components/GracePeriodBanner";
import { QuickActionFAB } from "@/components/QuickActionFAB";
import { NotificationBell } from "@/components/NotificationBell";
import { TwoFABanner } from "@/components/TwoFABanner";
import { OfflineBanner } from "@/components/OfflineBanner";
export default function DashboardLayout({ children }: { children: ReactNode }) {
  const { profile, role, profileLoading, isFounder } = useAuth();
  const isAdmin = role?.toLowerCase() === "admin";
  const isEmployee = role?.toLowerCase() === "employee";
  const isPro = useIsPro();
  const showReturningBanner = isAdmin && !isPro && !isFounder;

  return (
    <PaywallModalProvider>
      <BranchProvider>
      <SidebarProvider>
        <div className="min-h-screen flex w-full flex-col min-w-0">
          <OfflineBanner />
          <div className="flex flex-1 min-w-0">
            {isFounder ? <FounderSidebar /> : <AppSidebar />}
            <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
            <header className="h-14 flex items-center gap-2 sm:gap-4 border-b border-border px-3 sm:px-4 bg-card shrink-0">
              <SidebarTrigger className="shrink-0" />
              {isFounder ? (
                <span className="text-sm font-medium text-muted-foreground">Admin Console</span>
              ) : (
                <BranchSwitcher />
              )}
              <div className="flex items-center gap-2 ml-auto min-w-0">
                <NotificationBell />
                <span className="hidden sm:inline text-sm font-medium text-muted-foreground truncate max-w-[140px] md:max-w-none">
                  Powered by CHOLA'S REVIEW BOOST SERVICES
                </span>
              </div>
            </header>
            <main className="flex-1 p-4 sm:p-6 pb-24 relative min-w-0 overflow-x-auto">
              <QuickActionFAB />
              {profileLoading ? (
                <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
                  <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-muted-foreground">Setting up your dashboard…</p>
                </div>
              ) : (
                <>
                  <WelcomeToProScreen />
                    <div className="space-y-4">
                      <TwoFABanner />
                      <GracePeriodBanner />
                      {showReturningBanner && (
                        <ReturningUserBanner />
                      )}
                      {children}
                    </div>
                </>
              )}
            </main>
          </div>
        </div>
        <footer className="border-t border-border bg-card px-3 sm:px-4 py-4 shrink-0">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <p className="text-sm font-medium text-foreground">Need a Hand?</p>
              <p className="text-sm text-muted-foreground">
                Email:{" "}
                <a
                  href="mailto:support@reviewboost24.com"
                  className="text-primary hover:underline"
                >
                  support@reviewboost24.com
                </a>
              </p>
              <p className="text-xs text-muted-foreground">
                Our team typically responds within 1 hour.
              </p>
            </div>
            <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link to="/user-guide" className="hover:text-foreground transition-colors">User Guide</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            </nav>
          </div>
        </footer>
      </div>
    </SidebarProvider>
    </BranchProvider>
    </PaywallModalProvider>
  );
}
